/**
 * Train / Eval Split tab content for the Collection Detail page.
 *
 * Per `feature-spec.md` §2.4:
 *   - "Why split the gold dataset?" explainer banner
 *   - Collection-wide summary strip (finalised/training/evaluation/avg ratio)
 *   - "Split by Channel" overview list with inline per-channel `SplitEditor`
 *   - "Safeguards" section listing channels with `training < 10`
 *   - Draft-collection empty state
 *
 * Reuses `useChannelDatasets(collectionId)` — no new endpoint.
 */

import React, { useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { CHANNELS } from '../shared/channels';
import EmptyTabState from './components/EmptyTabState';
import { useChannelDatasets, useSaveSplit } from './hooks/useDatasets';
import type { ChannelDataset, Collection } from './types';
import SplitBar from './components/bars/SplitBar';
import SplitEditor from './components/split/SplitEditor';

interface Props {
  collection: Collection;
}

interface SummaryTotals {
  finalised: number;
  training: number;
  evaluation: number;
  avgEvalRatio: number;
}

const aggregate = (datasets: ChannelDataset[]): SummaryTotals => {
  const finalised = datasets.reduce((s, d) => s + d.stats.finalised, 0);
  const training = datasets.reduce(
    (s, d) => s + (d.split?.training ?? 0),
    0,
  );
  const evaluation = datasets.reduce(
    (s, d) => s + (d.split?.evaluation ?? 0),
    0,
  );
  const withSplit = datasets.filter((d) => d.split !== null);
  const avgEvalRatio =
    withSplit.length > 0
      ? withSplit.reduce((s, d) => s + (d.split?.ratio ?? 0), 0) /
        withSplit.length
      : 0;
  return { finalised, training, evaluation, avgEvalRatio };
};

const SplitTab: React.FC<Props> = ({ collection }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useChannelDatasets(
    collection.id,
    { enabled: collection.status === 'active' },
  );
  const saveSplit = useSaveSplit(collection.id);

  if (collection.status !== 'active') {
    return <EmptyTabState tabName="split" />;
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="split-tab-loading">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-lg border border-border/70 bg-card"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-[40vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">
          Failed to load channel datasets.
        </p>
        <Button variant="link" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const datasets = data ?? [];
  const totals = aggregate(datasets);
  const lowTraining = datasets.filter(
    (d) => d.split !== null && d.split.training < 10,
  );

  return (
    <div className="space-y-6" data-testid="split-tab">
      {/* Explainer banner */}
      <Alert data-testid="split-tab-explainer">
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          Training records are used as few-shot examples in classification
          prompts — the model sees these during inference. Evaluation records
          are held out and never seen by the model, ensuring unbiased
          performance measurement.
        </AlertDescription>
      </Alert>

      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="split-tab-summary">
        <SummaryStat
          value={totals.finalised.toLocaleString()}
          label="Total Finalised"
        />
        <SummaryStat
          value={totals.training.toLocaleString()}
          label="Total Training"
          toneClass="text-primary"
        />
        <SummaryStat
          value={totals.evaluation.toLocaleString()}
          label="Total Evaluation"
          toneClass="text-emerald-600"
        />
        <SummaryStat
          value={
            totals.avgEvalRatio > 0
              ? `${Math.round(totals.avgEvalRatio * 100)}%`
              : '—'
          }
          label="Avg Eval Ratio"
        />
      </div>

      {/* Per-channel overview */}
      <div className="space-y-3">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
          Split by Channel
        </h2>
        {datasets.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No channel datasets configured yet.
          </p>
        ) : (
          datasets.map((ds) => {
            const isEditing = editingId === ds.id;
            const channel = CHANNELS[ds.channelKey];
            return (
              <Card key={ds.id} data-testid={`split-tab-row-${ds.channelKey}`}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-8 w-1 shrink-0 rounded"
                      style={{ backgroundColor: channel.color }}
                      aria-hidden="true"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-foreground">
                        {channel.name}
                      </div>
                      <div className="mt-0.5 text-[12px] text-muted-foreground">
                        {ds.stats.finalised.toLocaleString()} finalised
                        {ds.split && (
                          <>
                            {' · '}
                            <span className="capitalize">{ds.split.method}</span>
                            {' · '}
                            <span className="capitalize">{ds.split.preset}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      variant={isEditing ? 'ghost' : 'secondary'}
                      size="sm"
                      onClick={() =>
                        setEditingId((prev) => (prev === ds.id ? null : ds.id))
                      }
                      data-testid={`split-tab-configure-${ds.channelKey}`}
                    >
                      {isEditing ? 'Close' : 'Configure Split'}
                    </Button>
                  </div>
                  {ds.split && !isEditing && (
                    <SplitBar
                      training={ds.split.training}
                      evaluation={ds.split.evaluation}
                      size="lg"
                    />
                  )}
                  {isEditing && (
                    <SplitEditor
                      dataset={ds}
                      isPending={saveSplit.isPending}
                      onCancel={() => setEditingId(null)}
                      onSave={(payload) =>
                        saveSplit.mutate(
                          { channelDatasetId: ds.id, payload },
                          { onSuccess: () => setEditingId(null) },
                        )
                      }
                    />
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Safeguards */}
      {lowTraining.length > 0 && (
        <div className="space-y-2" data-testid="split-tab-safeguards">
          <h2 className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wide text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Safeguards
          </h2>
          <ul className="space-y-1 rounded-md border-l-4 border-amber-600 bg-amber-50 p-3 text-[12px] text-amber-900">
            {lowTraining.map((ds) => {
              const channel = CHANNELS[ds.channelKey];
              return (
                <li
                  key={ds.id}
                  data-testid={`split-tab-safeguard-${ds.channelKey}`}
                >
                  <strong className="font-semibold">{channel.name}</strong> has{' '}
                  {ds.split?.training ?? 0} training records — below the
                  absolute minimum of 10.
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const SummaryStat: React.FC<{
  value: React.ReactNode;
  label: string;
  toneClass?: string;
}> = ({ value, label, toneClass = 'text-foreground' }) => (
  <Card>
    <CardContent className="p-5 text-center">
      <div className={`text-2xl font-bold leading-none tabular-nums ${toneClass}`}>
        {value}
      </div>
      <div className="mt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </CardContent>
  </Card>
);

export default SplitTab;
